import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  createYoutubeTestimonial,
  deleteYoutubeTestimonial,
  getAllTestimonials,
  updateYoutubeTestimonial,
} from "../../../services/operations/testimonialApi";

const initialForm = {
  name: "",
  courseTitle: "",
  rating: "5",
  review: "",
  youtubeUrl: "",
};

const AddVideoTestimonial = () => {
  const { token } = useSelector((state) => state.auth);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const loadTestimonials = useCallback(async () => {
    const testimonials = await getAllTestimonials();
    setItems((testimonials || []).filter((item) => item.youtubeUrl));
  }, []);

  useEffect(() => {
    loadTestimonials();
  }, [loadTestimonials]);

  const changeField = (event) => {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const saved = editingId
      ? await updateYoutubeTestimonial(editingId, form, token)
      : await createYoutubeTestimonial(form, token);
    if (saved) {
      setForm(initialForm);
      setEditingId(null);
      await loadTestimonials();
    }
    setSaving(false);
  };

  const editTestimonial = (item) => {
    setEditingId(item._id);
    setForm({
      name:item.username || "",
      courseTitle:item.courseTitle || "",
      rating:String(item.rating || 5),
      review:item.review || "",
      youtubeUrl:item.youtubeUrl || "",
    });
    window.scrollTo({ top:0, behavior:"smooth" });
  };

  const removeTestimonial = async (item) => {
    if (!window.confirm(`Delete ${item.username}'s video testimonial?`)) return;
    if (await deleteYoutubeTestimonial(item._id, token)) {
      if (editingId === item._id) {
        setEditingId(null);
        setForm(initialForm);
      }
      await loadTestimonials();
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6 text-richblack-5 sm:p-10">
      <h1 className="text-3xl font-bold">Add Video Testimonial</h1>
      <p className="mb-7 mt-2 text-richblack-300">
        Paste a YouTube video, Shorts, or youtu.be URL. It will appear as a
        compact playable testimonial.
      </p>
      <form onSubmit={submit} className="grid gap-5">
        <input
          name="name"
          value={form.name}
          onChange={changeField}
          placeholder="Student name"
          className="rounded bg-richblack-700 px-4 py-3"
          required
        />
        <input
          name="courseTitle"
          value={form.courseTitle}
          onChange={changeField}
          placeholder="Course or student title (optional)"
          className="rounded bg-richblack-700 px-4 py-3"
        />
        <select
          name="rating"
          value={form.rating}
          onChange={changeField}
          className="rounded bg-richblack-700 px-4 py-3"
        >
          {[5, 4, 3, 2, 1].map((rating) => (
            <option key={rating} value={rating}>
              {rating} star{rating === 1 ? "" : "s"}
            </option>
          ))}
        </select>
        <textarea
          name="review"
          rows={5}
          value={form.review}
          onChange={changeField}
          placeholder="Short testimonial text"
          className="rounded bg-richblack-700 px-4 py-3"
          required
        />
        <input
          type="url"
          name="youtubeUrl"
          value={form.youtubeUrl}
          onChange={changeField}
          placeholder="https://www.youtube.com/watch?v=..."
          className="rounded bg-richblack-700 px-4 py-3"
          required
        />
        <button
          type="submit"
          disabled={saving}
          className="rounded bg-yellow-100 px-5 py-3 font-semibold text-richblack-900 disabled:opacity-60"
        >
          {saving ? "Saving..." : editingId ? "Update Video Testimonial" : "Add Video Testimonial"}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={() => { setEditingId(null); setForm(initialForm); }}
            className="rounded border border-richblack-500 px-5 py-3 font-semibold"
          >
            Cancel editing
          </button>
        )}
      </form>

      <section className="mt-10 grid gap-4">
        <h2 className="text-2xl font-semibold">Existing Video Testimonials</h2>
        {items.length === 0 ? (
          <p className="text-richblack-300">No video testimonials added yet.</p>
        ) : items.map((item) => (
          <article key={item._id} className="grid gap-4 rounded-lg border border-richblack-600 bg-richblack-800 p-4 sm:grid-cols-[180px_1fr_auto] sm:items-center">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${item.youtubeUrl.split("v=")[1]}`}
              title={`${item.username} testimonial`}
              className="aspect-video w-full rounded border-0"
              loading="lazy"
            />
            <div>
              <h3 className="text-lg font-semibold">{item.username}</h3>
              <p className="text-sm text-richblack-300">{item.courseTitle} · {item.rating} stars</p>
              <p className="mt-2 text-sm text-richblack-200">{item.review}</p>
            </div>
            <div className="flex gap-2 sm:flex-col">
              <button type="button" onClick={() => editTestimonial(item)} className="rounded border border-yellow-100 px-4 py-2 text-yellow-100">Edit</button>
              <button type="button" onClick={() => removeTestimonial(item)} className="rounded border border-pink-300 px-4 py-2 text-pink-200">Delete</button>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
};

export default AddVideoTestimonial;
